<template>
<div>
    <button v-if="checked.length !== 0 && active_software === 'sitelink%'" class="primary setup-button" @click="show_setup_sitelink_tenants_modal = true">
        <span v-if="checked.length === 1">Setup 1 tenant</span>
        <span v-else>Setup {{ checked.length.toLocaleString() }} tenants</span>
    </button>

    <button v-else-if="checked.length !== 0 && active_software === 'storedge'" class="primary setup-button" @click="show_setup_storedge_tenants_modal = true">
        <span v-if="checked.length === 1">Setup 1 tenant</span>
        <span v-else>Setup {{ checked.length.toLocaleString() }} tenants</span>
    </button>

    <CheckChargesModal
        v-if="show_check_charges_modal && active_facility !== undefined"
        :facility="active_facility"
        @close="onCheckChargesModalClose" />

    <SetupSiteLinkTenantsModal
        v-if="show_setup_sitelink_tenants_modal"
        :tenants="checked"
        @close="onSetupSiteLinkTenantsModalClose" />

    <SetupStorEdgeTenantsModal
        v-if="show_setup_storedge_tenants_modal"
        :tenants="checked"
        @close="onSetupStorEdgeTenantsModalClose" />

    <div class="flex ai-c jc-sb mb-3">
        <div class="flex-0 ws-nw mr-2">Select a software:</div>

        <div class="flex-a ml-2">
            <select-input
                :active-index="active_software_index"
                :choices="software_options"
                :fixed="false"
                @change="onChangeSoftware"></select-input>
        </div>
    </div>

    <div class="flex ai-c jc-sb mb-3">
        <div class="flex-0 ws-nw mr-2">Select a facility:</div>

        <div class="flex-a ml-2">
            <select-input
                :active-index="active_facility_index"
                :choices="facility_options"
                :fixed="false"
                @change="onChangeFacility"></select-input>
        </div>
    </div>

    <div class="filter-table mb-3">
        <form @submit.prevent="loadTenants">
            <div class="flex ai-fe jc-sb">
                <div class="flex ai-fe fs-09 mr-3">
                    <div>
                        <button type="button" @click="onToggleAll">
                            <i v-show="checked.length === 0" class="material-icons">check_box_outline_blank</i>
                            <i v-show="checked.length !== 0 && checked.length === last_tenant_count" class="material-icons">check_box</i>
                            <i v-show="checked.length !== 0 && checked.length !== last_tenant_count" class="material-icons">indeterminate_check_box</i>
                        </button>
                    </div>

                    <div class="ml-3">
                        <label for="unit-filter">Unit</label>
                        <input id="unit-filter" type="text" v-model="tenant_filter.unit" />
                    </div>

                    <div class="ml-3">
                        <label for="name-filter">Tenant Name</label>
                        <input id="name-filter" type="text" v-model="tenant_filter.name" />
                    </div>

                    <div class="ml-3" style="width: 180px;">
                        <label>Error Filter</label>
                        <select-input
                            :disabled="active_facility_index === 0"
                            :active-index="active_error_filter_index"
                            :choices="error_filter_options"
                            @change="onChangeErrorFilter" />
                    </div>
                </div>

                <div>
                    <button :disabled="active_facility === undefined" type="button" class="small primary mr-2" @click="onExportTenants">
                        Export Tenants
                    </button>

                    <button :disabled="active_facility === undefined" type="button" class="small secondary" @click="show_check_charges_modal = true">
                        Check Charges
                    </button>

                    <span class="hint ml-2 mr-2">|</span>

                    <button class="mr-2" type="reset" @click="resetFilters">Reset</button>
                    <button type="submit">Search</button>
                </div>
            </div>
        </form>
    </div>

    <div class="flex ai-c jc-sb mb-3">
        <div class="flex-33"></div>

        <div class="ta-c flex-33">
            <vue-table-pagination
                ref="tenants_table_pagination"
                @vuetable-pagination:change-page="onChangePage">
            </vue-table-pagination>
        </div>

        <div class="ta-r flex-33">
            <vue-table-pagination-info ref="tenants_table_info"
                info-template="{total} total tenants"
                no-data-template="" />
        </div>
    </div>

    <div>
        <vue-table ref="tenants_table"
            :class="['table-loads', { loading: is_loading_tenants }]"
            api-url="/api/tenants/for-policy-setup"
            :load-on-start="false"
            :per-page="100"
            table-height="475px"
            :append-params="api_params"
            data-path="data.data"
            pagination-path="data.pagination"
            :http-options="http_options"
            :fields="columns"
            :data-manager="manageTableFunc"
            no-data-template="<span>No tenants to show.</span>"
            @vuetable:pagination-data="onPaginationData"
            @vuetable:loading="is_loading_tenants = true"
            @vuetable:loaded="is_loading_tenants = false"
            @vuetable:load-success="onTableLoadSuccess"
            @vuetable:load-error="onTableLoadError">
            <template slot="checkbox" slot-scope="props">
                <checkbox
                    :alone="true"
                    :center="true"
                    :checked="checked.includes(props.rowData)"
                    @click="toggleCheckbox(props.rowData)"></checkbox>
            </template>

            <template slot="name" slot-scope="props">
                <div class="flex jc-sb">
                    <span
                        v-if="props.rowData.last_name !== null"
                        class="row-name"
                        :title="`${props.rowData.last_name}, ${props.rowData.first_name}`">
                        {{ props.rowData.last_name }}, {{ props.rowData.first_name }}
                    </span>
                    <span
                        v-else
                        class="row-name"
                        :title="props.rowData.first_name">
                        {{ props.rowData.first_name }}
                    </span>

                    <span
                        v-show="props.rowData.tagged_for_removal"
                        class="as-c"
                        title="Flagged for deletion">
                        <i class="danger-text material-icons">flag</i>
                    </span>
                </div>
            </template>

            <template slot="coverage" slot-scope="props">
                ${{ props.rowData.coverage_premium }} per month for ${{ parseInt(props.rowData.coverage_coverage).toLocaleString() }}
            </template>

            <template slot="policy_errors" slot-scope="props">
                <div v-if="props.rowData.policy_errors_date_logged === null" class="gray-message">---</div>

                <div v-else class="cu-h" :title="props.rowData.policy_errors_date_logged">
                    {{ props.rowData.policy_errors_error_message }}

                    <span class="material-icons ml-1">event_note</span>
                </div>
            </template>
        </vue-table>
    </div>
</div>
</template>

<script>
import dayjs from "dayjs"
import http from "@/http"
import store from "@/store"
import Vue from "vue"
import CheckChargesModal from "../insurance-policy-management-page/CheckChargesModal"
import SetupSiteLinkTenantsModal from "../insurance-policy-management-page/SetupSiteLinkTenantsModal"
import SetupStorEdgeTenantsModal from "../insurance-policy-management-page/SetupStorEdgeTenantsModal"

export default {
    components: {
        CheckChargesModal,
        SetupSiteLinkTenantsModal,
        SetupStorEdgeTenantsModal
    },

    data () {
        return {
            active_error_filter_index: 0,
            active_facility_index: 0,
            active_software_index: 0,
            check_charges_facility: null,
            checked: [],
            columns: [
                {
                    name: "__slot:checkbox",
                    dataClass: "center aligned no-click",
                    width: "50px"
                },
                {
                    name: "unit",
                    sortField: "unit",
                    title: "Unit",
                    dataClass: "center aligned",
                    titleClass: "center aligned",
                    width: "125px"
                },
                {
                    name: "__slot:name",
                    sortField: "last_name",
                    title: "Name"
                },
                {
                    name: "__slot:coverage",
                    title: "Selected Coverage",
                    dataClass: "right aligned",
                    titleClass: "right aligned",
                    width: "250px"
                },
                {
                    name: "__slot:policy_errors",
                    title: "Last Error",
                    dataClass: "right aligned",
                    titleClass: "right aligned"
                }
            ],
            error_filter_options: [
                { key: "all", label: "Show All", badge: null },
                { key: "no_errors", label: "Non-Errors", badge: null },
                { key: "errors", label: "Errors", badge: null }
            ],
            facilities: [],
            http_options: {
                baseURL: this.$http.defaults.baseURL,
                headers: {
                    "X-Authorization": store.state.token.token
                },
                transformResponse: [(raw) => {
                    try {
                        return JSON.parse(raw)
                    } catch (error) {
                        this.load_tenants_error = "Unknown error loading tenants."

                        return raw
                    }
                }]
            },
            is_loading_tenants: false,
            last_tenant_count: 0,
            manageTableFunc: this.manageTable.bind(this),
            show_check_charges_modal: false,
            show_setup_sitelink_tenants_modal: false,
            show_setup_storedge_tenants_modal: false,
            software_options: [
                {
                    badge: null,
                    key: "sitelink%", // The "%" is used in the SQL LIKE clause.
                    label: "SiteLink"
                },
                {
                    badge: null,
                    key: "storedge",
                    label: "StorEdge"
                }
            ],
            tenant_filter: {
                name: null,
                unit: null
            }
        }
    },

    computed: {
        active_facility () {
            // This -1 is for the disabled option.
            return this.facilities[this.active_facility_index - 1]
        },

        active_software () {
            return this.software_options[this.active_software_index].key
        },

        api_params () {
            const params = {
                error_filter: this.error_filter_options[this.active_error_filter_index].key
            }

            if (this.active_facility) {
                params.facility_number = this.active_facility.facility_number
            }

            for (const key in this.tenant_filter) {
                const value = this.tenant_filter[key]

                if (value && value.length > 0) {
                    params[key] = value
                }
            }

            return params
        },

        facility_options () {
            return [{ badge: null, disabled: true, key: -1, label: "---" }].concat(this.facilities.map((facility) => {
                return {
                    badge: null,
                    key: facility.id,
                    label: facility.facility_number
                }
            }))
        }
    },

    watch: {
        active_facility_index (newValue) {
            if (newValue !== -1 && newValue !== 0) {
                this.loadTenants()
            } else {
                this.$refs.tenants_table.resetData()
            }
        },

        active_software_index (newValue) {
            if (newValue !== -1) {
                this.active_facility_index = 0
                this.loadFacilities()
            }
        }
    },

    mounted () {
        this.loadFacilities()
    },

    methods: {
        onChangeFacility (index) {
            this.active_facility_index = index
        },

        onChangeErrorFilter (index) {
            this.active_error_filter_index = index

            this.loadTenants()
        },

        onChangeSoftware (index) {
            this.active_software_index = index
        },

        /**
         * Change the pagination page when requested.
         *
         * @param object page Page
         *
         * @return null
         */
        onChangePage (page) {
            this.$refs.tenants_table.changePage(page)
        },

        onCheckChargesModalClose () {
            this.show_check_charges_modal = false
        },

        async onExportTenants () {
            this.$loadingManager.show(`Exporting tenants for ${this.active_facility.facility_number}...`)

            const params = new URLSearchParams()

            for (const key in this.api_params) {
                params.append(key, this.api_params[key])
            }

            try {
                const { data: { filename: fileKey } } = await http.get(`/api/tenants/export-policy-setup?${params}`)

                let downloadUrl = `${http.defaults.baseURL}/api/exports/download`
                downloadUrl += `?file_key=${fileKey}&filename=${this.active_facility.facility_number}_${dayjs().format("YYYY-MM-DD")}_policy_export`

                window.open(downloadUrl, "_blank")
            } catch (ex) {
                if (ex === undefined) {
                    this.$modalManager.add({ state: "bad", header: "Error", body: "Unexpected error!" })
                } else if (ex.message) {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex.message })
                } else {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex })
                }
            } finally {
                this.$loadingManager.hide()
            }
        },

        /**
         * Update the pagination component when data is received.
         *
         * @param object paginationData Pagination data
         *
         * @return null
         */
        onPaginationData (paginationData) {
            this.$refs.tenants_table_pagination.setPaginationData(paginationData)
            this.$refs.tenants_table_info.setPaginationData(paginationData)
        },

        onTableLoadError ({ response: { data } }) {
            console.warn(data)

            this.$eventManager.emit("add-toast", { message: "Error loading tenants", type: "error" })
        },

        /**
         * Get the loaded record count when the table successfully loads data.
         *
         * @param {object} data Server response data object
         */
        onTableLoadSuccess ({ data: { data: response } }) {
            this.last_tenant_count = response.data.length
        },

        onSetupSiteLinkTenantsModalClose () {
            this.show_setup_sitelink_tenants_modal = false
            this.checked = []

            this.loadTenants()
        },

        onSetupStorEdgeTenantsModalClose () {
            this.show_setup_storedge_tenants_modal = false
            this.checked = []

            this.loadTenants()
        },

        /**
         * Toggle selecting all tenants.
         *
         * @return null
         */
        onToggleAll () {
            const tenants = this.$refs.tenants_table.tableData

            if (tenants.length === this.checked.length) {
                this.checked = []
            } else {
                this.checked = tenants
            }
        },

        async loadFacilities () {
            try {
                const { data } = await http("/api/facilities/search", { params: {
                    software: this.active_software
                }})

                this.facilities = data
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            }
        },

        async loadTenants () {
            Vue.nextTick(() => {
                this.$refs.tenants_table.reload()
            })
        },

        /**
         * Set the sort field when the user requests sorting.
         *
         * @param object data Row data
         *
         * @return null
         */
        manageTable (data) {
            if (data.length) {
                this.sort_field = {
                    field: data[0].sortField,
                    direction: data[0].direction
                }
            } else {
                this.sort_field = {}
            }
        },

        resetFilters () {
            for (const key in this.tenant_filter) {
                this.tenant_filter[key] = null
            }

            this.loadTenants()
        },

        toggleCheckbox (tenant) {
            if (this.checked.includes(tenant) === false) {
                this.checked.push(tenant)
            } else {
                this.checked = this.checked.filter(checkedTenant => checkedTenant.id !== tenant.id)
            }
        }
    }
}
</script>

<style scoped>
.setup-button {
    background: #7ed5ad;
    border: 0;
    bottom: 20px;
    color: #606060;
    padding: 15px 20px;
    position: fixed;
    right: 20px;
    z-index: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/insurance-policy-management-page/SetupTenantsTab.vue